import React, { Component, createRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Input from "./Input";
import CESBotIcon from "../../Assets/svg/CESSVG.svg";
import {
  CustomerChatBotStreamAction,
  FetchCustomerChatBotAction,
} from "../../Actions/AI";

class ChatBotHome extends Component {
  constructor(props) {
    super(props);
    this.messagesEndRef = createRef();
    this.state = {
      isOpen: false,
    };
  }

  static propTypes = {
    CustomerChatBotStreamAction: PropTypes.func.isRequired,
    FetchCustomerChatBotAction: PropTypes.func.isRequired,
    messages: PropTypes.array,
    isStreaming: PropTypes.bool,
    streamingMessage: PropTypes.string,
  };

  componentDidMount() {
    this.props.FetchCustomerChatBotAction();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.messages !== this.props.messages ||
      prevProps.streamingMessage !== this.props.streamingMessage
    ) {
      this.scrollToBottom();
    }
  }

  scrollToBottom = () => {
    this.messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  handleSendMessage = (message) => {
    this.props.CustomerChatBotStreamAction(message);
  };

  toggleChat = () => {
    this.setState((prevState) => ({
      isOpen: !prevState.isOpen,
    }));
  };

  renderHeader = () => (
    <div className="flex flex-col items-center gap-2 bg-white p-4 shadow-custom-shadow">
      <div className="w-full max-w-4xl mx-auto flex items-center justify-between">
        <button
          onClick={this.toggleChat}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="flex items-center gap-3">
          <img src={CESBotIcon} alt="CES Bot" className="w-10 h-10" />
          <div className="font-semibold text-xl">CES BOT</div>
        </div>
        <div className="w-6"></div> {/* Spacer for alignment */}
      </div>
    </div>
  );

  renderWelcomeMessage = () => (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="flex items-start gap-2 mb-4">
        <img src={CESBotIcon} alt="CES Bot" className="w-8 h-8" />
        <div className="bg-blue-100 rounded-lg p-3 max-w-[80%]">
          <p>Hello there! 👋 It's nice to meet you!</p>
        </div>
      </div>
    </div>
  );

  renderLoadingIndicator = () => (
    <div className="flex items-start gap-2 mb-4">
      <img src={CESBotIcon} alt="CES Bot" className="w-8 h-8" />
      <div className="bg-blue-100 rounded-lg p-3 max-w-[80%] flex items-center space-x-1">
        <div
          className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
          style={{ animationDelay: "0ms" }}
        ></div>
        <div
          className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
          style={{ animationDelay: "150ms" }}
        ></div>
        <div
          className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
          style={{ animationDelay: "300ms" }}
        ></div>
      </div>
    </div>
  );

  renderMessages = () => {
    const { messages, isStreaming, streamingMessage } = this.props;

    return (
      <div className="w-full max-w-4xl mx-auto p-4">
        {messages &&
          messages.map((message) => (
            <div key={message.id} className="flex items-start gap-2 mb-4">
              {message.role === "bot" ? (
                <>
                  <img src={CESBotIcon} alt="CES Bot" className="w-8 h-8" />
                  <div className="bg-blue-100 rounded-lg p-3 max-w-[80%]">
                    <div
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2 mb-4 ml-auto">
                  <div className="bg-blue-500 text-white rounded-lg p-3 max-w-[80%]">
                    <p>{message.content}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        {isStreaming && streamingMessage ? (
          <div className="flex items-start gap-2 mb-4">
            <img src={CESBotIcon} alt="CES Bot" className="w-8 h-8" />
            <div className="bg-blue-100 rounded-lg p-3 max-w-[80%]">
              <div dangerouslySetInnerHTML={{ __html: streamingMessage }} />
            </div>
          </div>
        ) : isStreaming ? (
          this.renderLoadingIndicator()
        ) : null}
        <div ref={this.messagesEndRef} />
      </div>
    );
  };

  renderNavigationButtons = () => (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="grid grid-cols-3 gap-4">
        <button className="text-center px-6 py-3 border rounded-lg hover:bg-white transition-colors shadow-sm hover:shadow">
          Lay complaint
        </button>
        <button className="text-center px-6 py-3 border rounded-lg hover:bg-white transition-colors shadow-sm hover:shadow">
          Contact Support
        </button>
        <button className="text-center px-6 py-3 border rounded-lg hover:bg-white transition-colors shadow-sm hover:shadow">
          FAQ
        </button>
      </div>
    </div>
  );

  render() {
    const { isOpen } = this.state;
    const { messages } = this.props;

    if (!isOpen) {
      return (
        <button
          onClick={() => this.setState({ isOpen: true })}
          className="fixed bottom-4 right-4 bg-blue-500 text-white p-4 rounded-full shadow-lg hover:bg-blue-600 transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
        </button>
      );
    }

    return (
      <div className="fixed inset-0 w-full h-screen bg-white flex flex-col">
        {this.renderHeader()}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {messages && messages.length > 0
            ? this.renderMessages()
            : this.renderWelcomeMessage()}
          {this.renderNavigationButtons()}
        </div>
        <Input onSendMessage={this.handleSendMessage} disabled={this.props.isStreaming} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  messages: state.ai?.customerChatbot?.messages || [],
  isStreaming: state.ai?.customerChatbot?.isStreaming || false,
  streamingMessage: state.ai?.customerChatbot?.streamingMessage || "",
});

export default connect(mapStateToProps, {
  CustomerChatBotStreamAction,
  FetchCustomerChatBotAction,
})(ChatBotHome);
